import { useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import clsx from 'clsx'
import dayjs from 'dayjs'

import { ProcessState } from '@makyoapp/makyo-sdk/models/models'
import {
  ITableQuery,
  ITableSnapshotSettings,
  TableQueryOp,
  TableSortBy,
  TableSortOrder,
} from '@makyoapp/makyo-sdk/models/Table'
import { ColumnAdapter, fromSdkTable } from '@makyoapp/makyo-common'
import { Asc16, Desc16 } from '@makyoapp/makyo-icon/icon'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import {
  ButtonColor,
  Divider,
  Field,
  Modal,
  ModalGridFooter,
  SelectDropdown,
  SimpleInput,
  SingleIconSelect,
} from '@makyoapp/makyo-ui'

import { DEFAULT_LANGUAGE, IDBTable } from '@/core/db'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useSingleProjectContext } from '@/context/SingleProjectContext'
import { useIsProjectDb } from '@/hooks/useIsProjectDb'
import { useParamId } from '@/hooks/useParamId'
import { validateTableName } from '@/shared/ag-grid'
import { ORG_DATABASE_ROUTE, ORG_ROUTE, PROJECT_ROUTE, SHOW_SEARCH_LIMIT } from '@/shared/constants'
import { EVENT_KEYS } from '@/shared/custom-event'
import { appIconKeys } from '@/shared/icons'
import { ColumnsSelectToggle } from '@/components/ColumnsSelectToggle'
import { SnapshotFilterCondition } from '@/components/Condition/SnapshotFilterCondition'
import { Lbl } from '@/components/GridV2/components/fields/shared'
import { useGridV2Context } from '@/components/GridV2/contexts/GridV2Context'
import { RenderColumn } from '@/components/RenderColumn'
import { TablesDropdownV2 } from '@/components/TablesDropdownV2'
import { TzDd } from '@/components/TzDd'

const REFRESH_RATE_OPTIONS = [
  { value: 60 * 1000, label: '1m' },
  { value: 60 * 5 * 1000, label: '5m' },
  { value: 60 * 10 * 1000, label: '10m' },
  { value: 60 * 15 * 1000, label: '15m' },
  { value: 60 * 30 * 1000, label: '30m' },
  { value: 60 * 45 * 1000, label: '45m' },
  { value: 60 * 60 * 1000, label: '1h' },
]

const LIMIT_OPTIONS = [-1, 10, 25, 50, 100, 250, 500, 1000]

const initialSnapSettings = {
  tableName: '',
  refreshRate: REFRESH_RATE_OPTIONS[1].value,
  sourceTable: '',
  columns: [] as string[],
  limit: 0,
  sortBy: TableSortBy.Default,
  sortType: 'asc' as 'asc' | 'desc',
}

const initialQueryFilter: ITableQuery = {
  _Op: TableQueryOp.AND,
  _Statements: [],
}

const createInitialSnapSettings = (tablesMap: Map<string, IDBTable>, defaultTable?: IDBTable) => {
  if (defaultTable) {
    const settings = defaultTable.settings._NatureSettings as ITableSnapshotSettings
    const sourceTable = tablesMap.get(settings?._Source)
    if (settings && sourceTable) {
      return {
        tableName: defaultTable.settings._Name,
        refreshRate: settings._RefreshRate,
        sourceTable: sourceTable.id || '',
        columns: settings._Columns,
        limit: settings._Limit,
        sortBy:
          settings._Sort._By === TableSortBy.Default
            ? ''
            : settings._Sort._By === TableSortBy.Random
              ? '#random#'
              : settings._Sort._Column,
        sortType: settings._Sort?._Order as 'asc' | 'desc',
      }
    }
  }

  return initialSnapSettings
}

const createInitialQueryFilter = (defaultTable?: IDBTable): ITableQuery => {
  if (defaultTable) {
    const snapshotSettings = defaultTable.settings._NatureSettings as ITableSnapshotSettings
    if (snapshotSettings) {
      const query = snapshotSettings._Query || { _Op: 'AND', _Statements: [] }
      return {
        _Op: query._Op,
        _Statements: query._Statements.map((statement) => ({
          _Column: statement._Column,
          _Op: statement._Op,
          _Value1: statement._Value1 === 'string' ? { isCustom: true, value: '' } : statement._Value1,
          _Value2: statement._Value2 === 'string' ? { isCustom: true, value: '' } : statement._Value2,
          Options: null as any,
        })),
      }
    }
  }

  return initialQueryFilter
}

const getInitialTimezone = (activeTable?: IDBTable) => {
  return activeTable ? activeTable.settings._Tz : dayjs.tz.guess()
}

/**
 * Create/Update Snapshot Modal
 *
 * @todo:
 *  - everytime we update the snapshot settings, we will start a loading progress,
 *    show it in a modal (just like `Create DB AI` progress), it will show untill we received
 *    the next response from dbWorker after computing the source table
 */
export function SnapshotBuilderModal() {
  const { t } = useTranslation()
  const {
    dbId,
    tables,
    tablesMap,
    showSnapshotTable,
    setShowSnapshotTable,
    dbClient,
    setSnapshotLoadingSet,
    setShowCreateTableOptions,
  } = useDbClientContext()
  const { activeTable } = useGridV2Context()

  const isProject = useIsProjectDb()
  const { project } = useSingleProjectContext()
  const navigate = useNavigate()
  const orgId = useParamId('org')

  const showModal = showSnapshotTable === 'create' || showSnapshotTable === 'edit'
  const isEdit = showSnapshotTable === 'edit'

  // local state
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [nameError, setNameError] = useState('')
  const [loading, setLoading] = useState(false)

  // form state
  const [snapshotSettings, setSnapshotSettings] = useState(() =>
    createInitialSnapSettings(tablesMap, isEdit ? activeTable : undefined)
  )
  const [queryFilter, setQueryFilter] = useState(() => createInitialQueryFilter(isEdit ? activeTable : undefined))
  const [timezone, setTimezone] = useState(() => getInitialTimezone(isEdit ? activeTable : undefined))

  /**
   * Source table to snapshot
   */
  const sourceTable = useMemo(() => {
    if (!tablesMap.size || snapshotSettings.sourceTable === '') {
      return
    }
    return tablesMap.get(snapshotSettings.sourceTable)
  }, [tablesMap, snapshotSettings.sourceTable])

  /**
   * filtered column options
   */
  const columnOptions = useMemo(() => {
    const additionalOptions = [
      {
        id: '',
        type: 'table',
      },
      {
        id: '#random#',
        type: 'random',
      },
      '#div#',
      `#{${t('Sort By Column')}}#`,
    ] as unknown as ColumnAdapter[]

    return [...additionalOptions, ...(sourceTable ? sourceTable.GetColumns() : [])]
  }, [sourceTable, t])

  /**
   * create source column order map
   */
  const sourceColumnOrderMap = useMemo(() => {
    if (!sourceTable) return {}
    return sourceTable.GetColumns().reduce(
      (acc, c, idx) => {
        acc[c.id] = idx
        return acc
      },
      {} as Record<string, number>
    )
  }, [sourceTable])

  /**
   * handle column change
   * it will sort new columns based on source column order
   */
  const handleColumnChange = (newColumns: string[]) => {
    const sortedColumns = newColumns.sort((a, b) => {
      return sourceColumnOrderMap[a] - sourceColumnOrderMap[b]
    })
    setSnapshotSettings((prev) => ({ ...prev, columns: sortedColumns }))
  }

  /**
   * filter column options
   */
  const filterColumnOptions = useMemo(() => {
    return sourceTable?.GetColumns() || []
  }, [sourceTable])

  /**
   * reset state
   */
  const reset = useCallback(() => {
    setShowSnapshotTable(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const navigateToNewTable = useCallback(
    (tableId: string) => {
      if (orgId) {
        navigate(`${ORG_ROUTE}/${orgId}/${ORG_DATABASE_ROUTE}/${dbId}/${tableId}`)
      } else if (isProject) {
        navigate(`${PROJECT_ROUTE}/${project?.id}/${ORG_DATABASE_ROUTE}/${tableId}`)
      }
    },
    [orgId, isProject, navigate, dbId, project?.id]
  )

  const handleSubmit = () => {
    setShowConfirmModal(true)
  }

  const handleCloseConfirmation = async () => {
    if (loading) return

    setShowConfirmModal(false)
  }

  /**
   * Submit Snapshot on confirm
   */
  const handleConfirmSubmit = async () => {
    if (!dbClient || !sourceTable || !snapshotSettings.tableName.trim()) return

    setLoading(true)
    try {
      // Determine sort order
      const sortOrder = snapshotSettings.sortType === 'desc' ? TableSortOrder.Desc : TableSortOrder.Asc

      // Determine sort by type
      let sortBy = TableSortBy.Column
      if (snapshotSettings.sortBy === '') {
        sortBy = TableSortBy.Default
      } else if (snapshotSettings.sortBy === '#random#') {
        sortBy = TableSortBy.Random
      }

      let payload: ITableSnapshotSettings = {
        _RefreshRate: snapshotSettings.refreshRate,
        _Source: snapshotSettings.sourceTable,
        _Columns: snapshotSettings.columns,
        _Lang: DEFAULT_LANGUAGE,
        _Limit: snapshotSettings.limit,
        _Query: {
          _Op: queryFilter._Op,
          _Statements: queryFilter._Statements,
        },
        _Sort: {
          _By: sortBy,
          _Column:
            snapshotSettings.sortBy === TableSortBy.Default || snapshotSettings.sortBy === '#random#'
              ? ''
              : snapshotSettings.sortBy,
          _Order: sortOrder,
        },
        _Process: {
          _LastRanAt: new Date(),
          _LastRanErr: '',
          _State: ProcessState.Created,
        },
      }

      if (payload._Sort._By === TableSortBy.Column && payload._Sort._Column === '') {
        payload._Sort._By = TableSortBy.Default
      }
      if (payload._Sort._By === TableSortBy.Column && !payload._Columns.includes(payload._Sort._Column)) {
        payload._Sort._By = TableSortBy.Default
        payload._Sort._Column = ''
      }

      if (isEdit) {
        await activeTable?.UpdateSnapshotSettings(payload, timezone)
        // mark source table as loading
        const relatedSnapshots = dbClient.snapshotRefMap.get(payload._Source)
        if (relatedSnapshots) {
          setSnapshotLoadingSet((prev) => new Set([...prev, ...relatedSnapshots]))
        }
        toast.success('Snapshot updated!')
        //
      } else {
        const newTable = await dbClient.CreateSnapshotOrQueryTable(
          'snapshot',
          snapshotSettings.tableName.trim(),
          payload,
          timezone
        )
        window.dispatchEvent(new CustomEvent(EVENT_KEYS.DB_TABLE_CREATED, { detail: newTable.id }))

        // mark source table as loading
        const relatedSnapshots = dbClient.snapshotRefMap.get(payload._Source)
        if (relatedSnapshots) {
          setSnapshotLoadingSet((prev) => new Set([...prev, ...relatedSnapshots]))
        }
        navigateToNewTable(newTable.id)
        toast.success('Snapshot created!')
      }

      reset() // Close modal and reset form
      //
    } catch (error) {
      console.error('Failed to create snapshot table:', error)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Disabled state
   */
  const disabled = useMemo(() => {
    return snapshotSettings.columns.length < 1 || !sourceTable || !snapshotSettings.tableName.trim() || nameError !== ''
  }, [snapshotSettings.columns, sourceTable, snapshotSettings.tableName, nameError])

  return (
    <>
      <Modal fix show={showModal && !showConfirmModal} onHide={() => reset()} closeOnClickOutside={false}>
        <Modal.Header
          onCloseClick={() => {
            reset()
          }}
        >
          <div className='flex-c gap-2'>
            <MaterialIcon size={16} icon={appIconKeys.tableSnapshot} className='text-primary-500' />
            <Trans>{isEdit ? 'Edit Snapshot' : 'Snapshot Builder'}</Trans>
          </div>
        </Modal.Header>

        <Modal.Body className='modal-md min-h-[40vh] max-h-[70vh] overflow-y-auto'>
          <div>
            <Trans>
              The Snapshot Builder allows users to create database snapshots. Customize data selection, sorting, and
              limit columns for improved performance. Choose how often to refresh data, balancing up-to-date information
              with optimal database performance.
            </Trans>
          </div>

          <Divider />

          <div className='flex flex-col'>
            {!isEdit && (
              <Field>
                <Lbl required>
                  <Trans>Snapshot Name</Trans>
                </Lbl>
                <SimpleInput
                  error={t(nameError)}
                  autoFocus={true}
                  required
                  placeholder='Enter snapshot name'
                  value={snapshotSettings.tableName}
                  onChange={(e) => {
                    const val = e.currentTarget.value
                    setNameError(validateTableName(val, tables))
                    setSnapshotSettings({ ...snapshotSettings, tableName: val })
                  }}
                />
              </Field>
            )}

            {/* <Field>
            <Lbl>Refresh Rate</Lbl>
            <div className='flex-c flex-wrap'>
              {REFRESH_RATE_OPTIONS.map((rate) => {
                return (
                  <Chip
                    className='cursor-pointer'
                    key={rate.value}
                    active={rate.value === snapshotSettings.refreshRate}
                    onClick={() => {
                      setSnapshotSettings({ ...snapshotSettings, refreshRate: rate.value })
                    }}
                  >
                    <Clock16 />
                    <span>{rate.label}</span>
                  </Chip>
                )
              })}
            </div>
          </Field> */}

            <Field>
              <Lbl>Source</Lbl>

              <div className='w-full'>
                <TablesDropdownV2
                  showPlaceholder
                  value={snapshotSettings.sourceTable}
                  onChange={(value) => {
                    if (snapshotSettings.sourceTable === value) {
                      return
                    }

                    const table = tablesMap.get(value)
                    if (!table) return

                    setSnapshotSettings((prev) => ({
                      ...prev,
                      sourceTable: value,
                      columns: table?.GetColumns().map((c) => c.id) || [],
                    }))
                  }}
                  filterOption={(tables) => {
                    return tables.filter((tb) => !tb.isSnapshot && !tb.isQuery)
                  }}
                />
              </div>
            </Field>

            {sourceTable && (
              <>
                <Field>
                  <Lbl>
                    <Trans>Columns</Trans>
                  </Lbl>

                  <ColumnsSelectToggle
                    table={sourceTable}
                    values={snapshotSettings.columns}
                    onChange={handleColumnChange}
                  />
                </Field>

                <SnapshotFilterCondition
                  value={queryFilter}
                  onChange={setQueryFilter}
                  sourceColumns={filterColumnOptions}
                  sourceTable={fromSdkTable(sourceTable.settings)}
                />

                <Field>
                  <Lbl>
                    <Trans>Sort By</Trans>
                  </Lbl>
                  <SelectDropdown
                    id='new-snapshot-sort-by'
                    withSearch={columnOptions.length > SHOW_SEARCH_LIMIT}
                    portalMenu
                    menuContainerClassName='mk-ag-sdd'
                    menuClassName='min-w-[260px] text-m-m max-h-[200px] overflow-y-auto'
                    menuDefaultSize={false}
                    menuFullWidth={false}
                    menuZIndex='z-401'
                    value={columnOptions.find(({ id }) => id === snapshotSettings.sortBy)}
                    onChange={async (opt) => {
                      const val = (opt as ColumnAdapter)?.id
                      setSnapshotSettings({ ...snapshotSettings, sortBy: val })
                    }}
                    options={columnOptions}
                    optionLabel={(opt) => `${opt?.id} ${opt?.name} ${opt?.type}`}
                    renderOption={(opt, inToggle) => {
                      if (opt?.id === '' || (inToggle && typeof opt === 'string')) {
                        return (
                          <div className='flex-c'>
                            <MaterialIcon size={16} icon={appIconKeys.table} className='shrink-0' />
                            <Trans>Table Order</Trans>
                          </div>
                        )
                      }
                      if (opt?.id === '#random#') {
                        return (
                          <div className='flex-c'>
                            <MaterialIcon size={16} icon='shuffle' className='shrink-0' />
                            <Trans>Random</Trans>
                          </div>
                        )
                      }

                      return (
                        <RenderColumn
                          withStyle={false}
                          col={opt as ColumnAdapter}
                          className={clsx((opt as ColumnAdapter)?.id === '' && 'text-light')}
                        />
                      )
                    }}
                  />
                </Field>

                <Field>
                  <Lbl>
                    <Trans>Sort Type</Trans>
                  </Lbl>
                  <SingleIconSelect
                    onClick={(opt) => {
                      setSnapshotSettings({ ...snapshotSettings, sortType: opt.id as 'asc' | 'desc' })
                    }}
                    options={[
                      { id: 'asc', icon: <Asc16 />, title: t('Ascending') },
                      { id: 'desc', icon: <Desc16 />, title: t('Descending') },
                    ]}
                    selected={snapshotSettings.sortType}
                  />
                </Field>

                <Field>
                  <Lbl>
                    <Trans>Limit</Trans>
                  </Lbl>
                  <SelectDropdown
                    onChange={(opt) => {
                      setSnapshotSettings({ ...snapshotSettings, limit: opt === -1 ? 0 : opt })
                    }}
                    value={snapshotSettings.limit === 0 ? -1 : snapshotSettings.limit}
                    portalMenu
                    menuContainerClassName='mk-ag-sdd'
                    menuClassName='min-w-[260px] text-m-m max-h-[200px] overflow-y-auto'
                    menuDefaultSize={false}
                    menuFullWidth={false}
                    menuZIndex='z-305'
                    options={LIMIT_OPTIONS}
                    renderOption={(opt) => {
                      return opt === -1 ? (
                        <div>
                          <Trans>No Limit</Trans>
                        </div>
                      ) : (
                        <div>{opt}</div>
                      )
                    }}
                  />
                </Field>

                <Field>
                  <Lbl>
                    <Trans>Timezone</Trans>
                  </Lbl>

                  <TzDd id='snapshot-builder-timezone' value={timezone} onChange={setTimezone} withAuto={false} />
                </Field>
              </>
            )}
          </div>
        </Modal.Body>

        <ModalGridFooter
          onHide={reset}
          onCancel={reset}
          onSubmit={handleSubmit}
          cancelLabel={t('Cancel')}
          primaryLabel={isEdit ? t('Save') : t('Create')}
          disabled={disabled}
          isLoading={loading}
        />
      </Modal>

      <Modal fix show={showConfirmModal} onHide={handleCloseConfirmation} closeOnClickOutside={false}>
        <Modal.Header onCloseClick={handleCloseConfirmation}>
          <div className='flex-c gap-2'>
            <MaterialIcon size={16} icon={appIconKeys.tableSnapshot} className='text-primary-500' />
            <Trans>Confirmation</Trans>
          </div>
        </Modal.Header>

        <Modal.Body className='modal-md min-h-[175px] overflow-y-auto'>
          <div className='font-semibold mb-2'>
            Are you sure you want to {isEdit ? 'update' : 'create'} this snapshot?
          </div>
          <div>
            {isEdit ? 'Updating the snapshot settings' : 'Creating a new snapshot'} will trigger a re-computation
            process. It might take some time to complete, depending on the size of the source table.
          </div>
        </Modal.Body>

        <ModalGridFooter
          onHide={handleCloseConfirmation}
          onCancel={handleCloseConfirmation}
          onSubmit={handleConfirmSubmit}
          cancelLabel={t('Go Back')}
          primaryLabel={t('Confirm')}
          colorStyle={ButtonColor.Secondary}
          disabled={disabled}
          isLoading={loading}
        />
      </Modal>
    </>
  )
}
